import { ApiResult, HttpClient } from "../http.js";

/**
 * Supplier sortiment (Phase 4). Active org = the supplier company; needs `supplier.catalog.manage`.
 * Refusals carry a stable `code` (see ApiError.code / body.code): publish 409 `nothing_to_publish`,
 * prices 409 `invalid_price`, subscribe 409 `already_subscribed`; 404 `catalog_not_found` | `grant_not_found`.
 */

export interface SupplierCatalogDto {
  id: string;
  name: string;
  currency: string;
  draftVersion: number;
  publishedVersion: number;
  publishedAt: string | null;
  lastUploadFileName: string | null;
  lastUploadAt: string | null;
  lastUploadRowCount: number | null;
  itemCount: number;
  subscriberCount: number;
}

/** POST .../upload answers 200 with `succeeded=false` and a Danish `errorMessage` for a bad file. */
export interface CatalogUploadResultDto {
  catalogId: string;
  succeeded: boolean;
  version: number;
  rowsParsed: number;
  rowsImported: number;
  rowsSkipped: number;
  rowsRetired: number;
  errorMessage: string | null;
}

export interface SupplierCatalogItemDto {
  itemNumber: string;
  name: string;
  unit: string | null;
  listPriceExclVat: number | null;
  defaultNetPriceExclVat: number | null;
  ean: string | null;
  groupCode: string | null;
}

export interface PagedSupplierCatalogItemsDto {
  items: SupplierCatalogItemDto[];
  page: number;
  pageSize: number;
  total: number;
}

/** `itemNumber = null` is the whole-catalogue discount. Exactly one of net price / discount is set. */
export interface SupplierCustomerPriceDto {
  itemNumber: string | null;
  netPriceExclVat: number | null;
  discountPct: number | null;
}

/** `discountPct` = the whole-catalogue discount; `netPriceCount` = items with their own net price, `itemDiscountCount` = items with their own discount. */
export interface SupplierCustomerPriceSummaryDto {
  discountPct: number | null;
  netPriceCount: number;
  /** Items with their own percentage discount. Optional: older answers lack it. */
  itemDiscountCount?: number;
}

export type CatalogSubscriptionStatus = "Offered" | "Active" | "Unsubscribed";

export interface SupplierCatalogSubscriptionDto {
  customerCompanyId: string;
  customerName: string;
  status: CatalogSubscriptionStatus;
  lastSyncedVersion: number;
  lastSyncedAt: string | null;
  lastError: string | null;
  /** Customer prices in one line (no extra call). Absent on a backend that predates it. */
  priceSummary?: SupplierCustomerPriceSummaryDto | null;
}

export interface SearchSupplierCatalogItemsQuery {
  search?: string;
  page?: number;
  pageSize?: number;
}

export class SupplierCatalogApi {
  constructor(private readonly http: HttpClient) {}

  list(): Promise<ApiResult<SupplierCatalogDto[]>> {
    return this.http.get<SupplierCatalogDto[]>("/api/supplier/catalogs");
  }

  create(name: string): Promise<ApiResult<SupplierCatalogDto>> {
    return this.http.post<SupplierCatalogDto>("/api/supplier/catalogs", { body: { name } });
  }

  /** `form` holds a single `file` field (Bygma CSV). The caller builds the FormData on its platform. */
  upload(id: string, form: FormData): Promise<ApiResult<CatalogUploadResultDto>> {
    return this.http.post<CatalogUploadResultDto>(`/api/supplier/catalogs/${encodeURIComponent(id)}/upload`, { body: form });
  }

  publish(id: string): Promise<ApiResult<SupplierCatalogDto>> {
    return this.http.post<SupplierCatalogDto>(`/api/supplier/catalogs/${encodeURIComponent(id)}/publish`);
  }

  items(id: string, query: SearchSupplierCatalogItemsQuery = {}): Promise<ApiResult<PagedSupplierCatalogItemsDto>> {
    // Hand-built query string: this package is typechecked without the DOM lib (see price-lists.ts).
    const params: string[] = [];
    if (query.search) params.push(`search=${encodeURIComponent(query.search)}`);
    params.push(`page=${query.page ?? 1}`, `pageSize=${query.pageSize ?? 50}`);
    return this.http.get<PagedSupplierCatalogItemsDto>(`/api/supplier/catalogs/${encodeURIComponent(id)}/items?${params.join("&")}`);
  }

  customerPrices(id: string, customerCompanyId: string): Promise<ApiResult<SupplierCustomerPriceDto[]>> {
    return this.http.get<SupplierCustomerPriceDto[]>(this.pricesPath(id, customerCompanyId));
  }

  setCustomerPrice(id: string, customerCompanyId: string, price: SupplierCustomerPriceDto): Promise<ApiResult<SupplierCustomerPriceDto>> {
    return this.http.put<SupplierCustomerPriceDto>(this.pricesPath(id, customerCompanyId), { body: price });
  }

  /** No `itemNumber` removes the whole-catalogue discount. */
  deleteCustomerPrice(id: string, customerCompanyId: string, itemNumber?: string): Promise<ApiResult<void>> {
    const query = itemNumber ? `?itemNumber=${encodeURIComponent(itemNumber)}` : "";
    return this.http.delete<void>(`${this.pricesPath(id, customerCompanyId)}${query}`);
  }

  subscriptions(id: string): Promise<ApiResult<SupplierCatalogSubscriptionDto[]>> {
    return this.http.get<SupplierCatalogSubscriptionDto[]>(`/api/supplier/catalogs/${encodeURIComponent(id)}/subscriptions`);
  }

  subscribe(id: string, customerCompanyId: string): Promise<ApiResult<SupplierCatalogSubscriptionDto>> {
    return this.http.post<SupplierCatalogSubscriptionDto>(`/api/supplier/catalogs/${encodeURIComponent(id)}/subscriptions`, {
      body: { customerCompanyId },
    });
  }

  unsubscribe(id: string, customerCompanyId: string): Promise<ApiResult<void>> {
    return this.http.delete<void>(
      `/api/supplier/catalogs/${encodeURIComponent(id)}/subscriptions/${encodeURIComponent(customerCompanyId)}`,
    );
  }

  private pricesPath(id: string, customerCompanyId: string): string {
    return `/api/supplier/catalogs/${encodeURIComponent(id)}/customers/${encodeURIComponent(customerCompanyId)}/prices`;
  }
}
